import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import {
  LocationAction,
  CreateLocationActionData,
  UpdateLocationActionData,
  LocationActionTriggerResponse,
  LocationActionTestResponse,
} from '../types';

export interface LocationActionsFilters {
  include_inactive?: boolean;
}

interface LocationActionsResponse {
  success: boolean;
  location_id: number;
  items: LocationAction[];
  count: number;
}

class LocationActionService {
  /**
   * Get all actions for a location
   */
  async getLocationActions(
    locationId: number,
    filters?: LocationActionsFilters
  ): Promise<LocationAction[]> {
    const params = filters ? `?include_inactive=${filters.include_inactive ?? true}` : '?include_inactive=true';
    const response = await apiClient.get<LocationActionsResponse>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTIONS}/${locationId}/actions${params}`
    );
    return response.items || [];
  }

  /**
   * Get a single action by ID
   */
  async getActionById(actionId: number): Promise<LocationAction> {
    return apiClient.get<LocationAction>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTION}/${actionId}`
    );
  }

  /**
   * Create a new action for a location
   */
  async createAction(
    locationId: number,
    data: CreateLocationActionData
  ): Promise<LocationAction> {
    return apiClient.post<LocationAction>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTIONS}/${locationId}/actions`,
      data
    );
  }

  /**
   * Update an existing action
   */
  async updateAction(
    actionId: number,
    data: UpdateLocationActionData
  ): Promise<LocationAction> {
    return apiClient.put<LocationAction>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTION}/${actionId}`,
      data
    );
  }

  /**
   * Delete an action
   */
  async deleteAction(actionId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTION}/${actionId}`
    );
  }

  /**
   * Trigger an action (e.g., open gate, call 911)
   */
  async triggerAction(actionId: number): Promise<LocationActionTriggerResponse> {
    return apiClient.post<LocationActionTriggerResponse>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTION}/${actionId}/trigger`
    );
  }

  /**
   * Test action connectivity (e.g., ping device)
   */
  async testAction(actionId: number): Promise<LocationActionTestResponse> {
    return apiClient.post<LocationActionTestResponse>(
      `${API_CONFIG.ENDPOINTS.LOCATION_ACTION}/${actionId}/test`
    );
  }

  /**
   * Toggle action active status
   */
  async toggleActionStatus(
    actionId: number,
    isActive: boolean
  ): Promise<LocationAction> {
    return this.updateAction(actionId, { is_active: isActive });
  }
}

export const locationActionService = new LocationActionService();
